/**
 * Admin · Products & Services catalog (Phase 1)
 *
 * Minimal table + add/edit drawer with image upload. Customer-facing proposal
 * viewer is intentionally Phase 2 work — this screen exists so admins can
 * seed the catalog and confirm thumbnails render before the customer flow
 * is built.
 */
import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Plus, Save, Upload, X, Sparkles, Lock, Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { resolveImageUrl } from "@/lib/imageUrl";

const STATUS_OPTIONS = [
  { value: "required",    label: "Required" },
  { value: "optional",    label: "Optional" },
  { value: "recommended", label: "Recommended" },
];

const empty = {
  name: "", category: "", short_description: "",
  long_description: "", detail_url: "", slug: "",
  unit_price_cents: 0, default_qty: 1, gst_included: true,
  default_status: "optional", active: true,
};

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [matchInfo, setMatchInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [drawer, setDrawer] = useState(null);  // { id?, ...fields }
  const [saving, setSaving] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [imgBroken, setImgBroken] = useState(false);
  // Feb 2026 — recommendation-key registry (canonical list from the
  // backend). Powers the multi-select chip picker in the drawer that
  // tags each product with the rule keys it satisfies. Fetched once
  // per page load.
  const [recKeys, setRecKeys] = useState([]);
  // Feb 2026 — inline "Preview matcher" panel state. Lives in the
  // drawer so admins can sanity-check tag coverage without leaving the
  // product edit screen. `preview` is null when hidden, `{loading, data}`
  // when a request is in flight or complete.
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  // Reset the URL input + broken-image flag whenever a new product opens
  // in the drawer (so prior input/error state doesn't carry over).
  useEffect(() => {
    if (drawer) {
      setImageUrlInput(drawer.image_url || "");
      setImgBroken(false);
      setPreview(null);
    }
  }, [drawer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/products"),
      api.get("/admin/products/match-info").catch(() => ({ data: {} })),
    ]).then(([prods, mi]) => {
      setRows(prods.data);
      setMatchInfo(mi.data || {});
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  // Fetch the recommendation-key registry once so the drawer picker
  // can render even before the first product opens. Non-blocking — if
  // it fails we just skip the picker (still fully functional).
  useEffect(() => {
    api.get("/admin/recommendation-keys")
      .then((r) => setRecKeys(r.data?.keys || []))
      .catch(() => setRecKeys([]));
  }, []);

  const openCreate = () => setDrawer({ ...empty });
  const openEdit = (p) => setDrawer({ ...p });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: drawer.name,
        category: drawer.category || null,
        short_description: drawer.short_description || null,
        long_description: drawer.long_description || null,
        detail_url: (drawer.detail_url || "").trim() || null,
        slug: (drawer.slug || "").trim() || null,
        unit_price_cents: Number(drawer.unit_price_cents) || 0,
        default_qty: Number(drawer.default_qty) || 1,
        gst_included: !!drawer.gst_included,
        default_status: drawer.default_status,
        active: !!drawer.active,
        // Feb 2026 — explicit test-recommendation tags. Empty array is
        // meaningful ("intentionally untagged"), so we always send the
        // field rather than omitting when the list is empty.
        recommendation_keys: Array.isArray(drawer.recommendation_keys)
          ? drawer.recommendation_keys
          : [],
      };
      // Phase 2L-A59 — silent-drop fix. Previously, typing a URL into
      // the "Use URL" input and clicking Save instead of "Use URL"
      // would lose the URL. The user thought the image was saved; in
      // fact only the text fields were sent and image_url stayed null.
      // Now: if the user typed a URL that's different from the
      // persisted drawer.image_url, fold it into the save payload so
      // a single click reliably persists both text + image. The backend
      // (_validate_image_url) rejects invalid URLs with 400 so the
      // user always gets clear feedback.
      const typedUrl = (imageUrlInput || "").trim();
      const persistedUrl = (drawer.image_url || "").trim();
      if (typedUrl && typedUrl !== persistedUrl) {
        payload.image_url = typedUrl;
      }
      let saved;
      if (drawer.id) {
        const { data } = await api.patch(`/admin/products/${drawer.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post(`/admin/products`, payload);
        saved = data;
      }
      toast.success(drawer.id ? "Product updated" : "Product created");
      setDrawer(saved);
      // Keep the URL input in lockstep so it visually reflects the
      // persisted state immediately after save.
      setImageUrlInput(saved.image_url || "");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!drawer?.id) {
      toast.error("Save the product first, then add an image");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/products/${drawer.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDrawer((d) => ({ ...d, image_url: data.image_url }));
      load();
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  // Phase 2L-A49 — paste a remote image URL (validated server-side).
  const applyImageUrl = async (url) => {
    if (!drawer?.id) {
      toast.error("Save the product first, then add an image URL");
      return;
    }
    const trimmed = (url || "").trim();
    if (!trimmed) {
      toast.error("Enter an image URL");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/products/${drawer.id}`, {
        image_url: trimmed,
      });
      setDrawer((d) => ({ ...d, image_url: data.image_url }));
      load();
      toast.success("Image URL saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid image URL");
    }
  };

  // Phase 2L-A49 — clear the product image (also removes local file).
  const removeImage = async () => {
    if (!drawer?.id) return;
    if (!window.confirm("Remove the current product image? The product will fall back to the placeholder.")) return;
    try {
      await api.delete(`/admin/products/${drawer.id}/image`);
      setDrawer((d) => ({ ...d, image_url: null }));
      setImageUrlInput("");
      load();
      toast.success("Image removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? Existing accepted estimates will keep their snapshot.`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deactivated");
    load();
  };

  // Feb 2026 — bulk hard-delete every inactive product that isn't
  // referenced by any proposal. Companion to the single-item
  // `removePermanent` — same reference-safety guard, applied server-side
  // in one round-trip so the admin doesn't have to click 141 times.
  //
  // ``cascade`` (default false): when true, the endpoint first PURGES
  // every DRAFT / CANCELLED / REJECTED / EXPIRED proposal that
  // references any inactive product, then re-runs the reference check.
  // Sent / viewed / approved proposals are always preserved.
  const bulkDeleteInactive = async ({ cascade = false } = {}) => {
    const inactiveCount = rows.filter((r) => !r.active).length;
    if (!inactiveCount) return;
    const heading = cascade
      ? `PURGE test-data drafts + PERMANENTLY delete every inactive product?`
      : `PERMANENTLY delete every inactive product?`;
    const body = cascade
      ? (
          `${inactiveCount} inactive item(s) will be checked.\n\n` +
          `First: every DRAFT/CANCELLED/REJECTED/EXPIRED proposal that ` +
          `references any inactive product will be DELETED.\n\n` +
          `Then: every product with no remaining references will be ` +
          `permanently removed.\n\n` +
          `Sent / viewed / approved proposals are NEVER touched.\n\n` +
          `This CANNOT be undone. Type DELETE on the next dialog to proceed.`
        )
      : (
          `${inactiveCount} inactive item(s) will be checked. Products still ` +
          `referenced by any proposal will be SKIPPED automatically — the rest ` +
          `will be removed for good.\n\n` +
          `This CANNOT be undone. Type DELETE on the next dialog to proceed.`
        );
    const confirmed = window.confirm(`${heading}\n\n${body}`);
    if (!confirmed) return;
    const doubleCheck = window.prompt(
      `Type DELETE to confirm ${cascade ? "cascade" : "safe"} bulk cleanup`
    );
    if ((doubleCheck || "").trim().toUpperCase() !== "DELETE") {
      toast.info("Bulk delete cancelled");
      return;
    }
    setBulkDeleting(true);
    try {
      const url = `/admin/products/bulk-delete-inactive${cascade ? "?cascade_drafts=true" : ""}`;
      const { data } = await api.post(url);
      const parts = [];
      if (cascade && data?.cascade_purged) parts.push(`Purged ${data.cascade_purged} draft proposal(s)`);
      parts.push(`deleted ${data?.deleted ?? 0} product(s)`);
      if (data?.blocked) parts.push(`skipped ${data.blocked} (still customer-facing)`);
      toast.success(parts.join(" · "), { duration: 8000 });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Feb 2026 — permanent delete for archived (inactive) products. Refuses
  // when the item is still referenced by any proposal so we can't leave
  // dangling foreign keys. Backend returns HTTP 409 with the offending
  // proposal count so we can surface an actionable error to the admin.
  const removePermanent = async (p) => {
    const confirmed = window.confirm(
      `PERMANENTLY delete "${p.name}"?\n\n` +
      `This CANNOT be undone. The product record and its image (if any) will be removed from the database.\n\n` +
      `The delete will be blocked if any proposal still references this product.\n\n` +
      `Type OK on the next dialog to proceed.`
    );
    if (!confirmed) return;
    const doubleCheck = window.prompt(`Type DELETE to confirm permanent removal of "${p.name}"`);
    if ((doubleCheck || "").trim().toUpperCase() !== "DELETE") {
      toast.info("Permanent delete cancelled");
      return;
    }
    try {
      const { data } = await api.delete(`/admin/products/${p.id}/permanent`);
      toast.success(`Permanently deleted "${data?.name || p.name}"`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "product_referenced_by_proposals") {
        toast.error(
          `Blocked — ${detail.proposal_count} proposal(s) still reference this product. Archive or edit those first.`,
          { duration: 8000 }
        );
      } else {
        toast.error(formatApiError(detail) || "Permanent delete failed");
      }
    }
  };

  const apiOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin · Catalog</div>
            <h1 data-testid="admin-products-heading" className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Products &amp; Services</h1>
            <p className="text-sm text-slate-500 mt-2">
              Seed the catalog used to build customer proposals. Each item supports a thumbnail, price, default quantity and required/optional status.
            </p>
          </div>
          <Button data-testid="admin-products-new" onClick={openCreate} className="rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> New product / service
          </Button>
        </div>

        {/* Active/inactive filter + counts. Defaults to active-only so the
            catalog screen no longer drowns admins in 488 historical test
            items. Inactive items remain accessible via the toggle for
            audit purposes and one-click reactivation. */}
        {!loading && (
          <div data-testid="admin-products-filter" className="flex items-center justify-between mb-3 gap-3">
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-900">{rows.filter((r) => r.active).length}</span> active
              <span className="text-slate-400"> · </span>
              <span className="font-semibold text-slate-500">{rows.filter((r) => !r.active).length}</span> inactive
            </div>
            <div className="flex items-center gap-2">
              {showInactive && rows.filter((r) => !r.active).length > 0 && (
                <>
                  <button
                    type="button"
                    data-testid="admin-products-bulk-delete-inactive"
                    onClick={() => bulkDeleteInactive({ cascade: false })}
                    disabled={bulkDeleting}
                    title="Permanently delete every inactive product not referenced by any proposal"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white hover:border-rose-600 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-3 h-3" />
                    {bulkDeleting ? "Deleting…" : `Delete all ${rows.filter((r) => !r.active).length} inactive`}
                  </button>
                  <button
                    type="button"
                    data-testid="admin-products-bulk-delete-cascade"
                    onClick={() => bulkDeleteInactive({ cascade: true })}
                    disabled={bulkDeleting}
                    title="Purge draft / cancelled proposals that reference inactive products, then delete all inactive. Sent / approved proposals are preserved."
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-rose-500 bg-rose-600 text-white hover:bg-rose-700 hover:border-rose-700 transition disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                  >
                    <Trash2 className="w-3 h-3" />
                    Purge drafts + delete all
                  </button>
                </>
              )}
              <button
                type="button"
                data-testid="admin-products-toggle-inactive"
                onClick={() => setShowInactive((v) => !v)}
                className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 px-3 py-1.5 rounded-full border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 transition"
              >
                {showInactive ? "Hide inactive" : "Show inactive"}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No products yet — add one to get started.</p>
            </div>
          ) : (
            <table data-testid="admin-products-table" className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Thumb</th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Category</th>
                  <th className="text-right px-4 py-3 font-semibold">Price</th>
                  <th className="text-left px-4 py-3 font-semibold">Default</th>
                  <th className="text-left px-4 py-3 font-semibold">Active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.filter((p) => showInactive || p.active).map((p) => {
                  const mi = matchInfo[p.id] || {};
                  const isSuggested = p.active && mi.is_first_match;
                  return (
                  <tr key={p.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      {p.image_url ? (
                        <img
                          src={resolveImageUrl(p.image_url)}
                          alt=""
                          className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                          onError={(e) => {
                            // Replace with placeholder div on broken URL so the
                            // catalogue stays usable even if a remote URL rots.
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextElementSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="w-12 h-12 bg-slate-100 rounded-lg border border-slate-200 items-center justify-center"
                        style={{ display: p.image_url ? "none" : "flex" }}
                      >
                        <ImageIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{p.name}</span>
                        {p.is_canonical_seed && (
                          <span title="Canonical PWS catalog item" data-testid={`prod-canonical-${p.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded-full">
                            <Lock className="w-2.5 h-2.5" /> Canonical
                          </span>
                        )}
                        {isSuggested && (
                          <span title="Lab → Estimate matcher will pick this item" data-testid={`prod-suggested-${p.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            <Sparkles className="w-2.5 h-2.5" /> Suggested
                          </span>
                        )}
                        {p.price_is_placeholder && (
                          <span title="Placeholder price — admin to confirm" className="text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            Placeholder
                          </span>
                        )}
                      </div>
                      {p.short_description && <div className="text-xs text-slate-500 line-clamp-1 max-w-md mt-0.5">{p.short_description}</div>}
                      {isSuggested && mi.labels?.length > 0 && (
                        <div className="text-[10px] text-emerald-700 mt-1 font-semibold">
                          Matched by: {mi.labels.join(" · ")}
                        </div>
                      )}
                      {/* Feb 2026 — explicit recommendation-key tags. Renders
                          nothing when the product has no tags so the row
                          height doesn't jump unnecessarily. */}
                      {(p.recommendation_keys || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`prod-rec-key-tags-${p.id}`}>
                          {(p.recommendation_keys || []).map((k) => (
                            <span
                              key={k}
                              title={k}
                              className="text-[9px] uppercase tracking-wider font-bold text-cyan-800 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded-full"
                            >
                              {k.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{p.category || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <div>${(p.unit_price_cents / 100).toFixed(2)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        {p.gst_included ? "inc GST" : "+ GST"}
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">{p.default_status}</td>
                    <td className="px-4 py-3">{p.active ? <span className="text-emerald-600 text-xs font-semibold">ACTIVE</span> : <span className="text-slate-400 text-xs">inactive</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(p)} className="text-cyan-700 hover:text-cyan-900 text-sm font-semibold mr-3">Edit</button>
                      {p.active
                        ? <button onClick={() => remove(p)} className="text-rose-600 hover:text-rose-800 text-sm font-semibold">Deactivate</button>
                        : (
                          <>
                            <button
                              data-testid={`prod-reactivate-${p.id}`}
                              onClick={async () => {
                                await api.patch(`/admin/products/${p.id}`, { active: true });
                                toast.success("Reactivated");
                                load();
                              }}
                              className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold mr-3"
                            >Reactivate</button>
                            <button
                              data-testid={`prod-delete-permanent-${p.id}`}
                              onClick={() => removePermanent(p)}
                              title="Permanently remove this product from the catalog"
                              className="inline-flex items-center gap-1 text-rose-700 hover:text-white hover:bg-rose-600 text-xs font-semibold px-2 py-1 rounded-md border border-rose-200 hover:border-rose-600 transition"
                            >
                              <Trash2 className="w-3 h-3" /> Delete permanently
                            </button>
                          </>
                        )}
                    </td>
                  </tr>);
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {drawer && (
        <div data-testid="admin-products-drawer" className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-semibold">{drawer.id ? "Edit product" : "New product / service"}</h2>
              <button onClick={() => setDrawer(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>

            {drawer.id && (() => {
              const mi = matchInfo[drawer.id];
              if (!mi || mi.labels?.length === 0) {
                return drawer.active ? (
                  <div data-testid="prod-drawer-not-matched" className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600">
                    <div className="font-semibold uppercase tracking-wider text-[10px] text-slate-500 mb-0.5">Lab → Estimate matcher</div>
                    Not currently matched by any recommendation rule. Adjust the name or category to add a keyword like "BioCleanse", "UV", "Calcite", "Softener", "Tank cleaning", "Aeration", "Cartridge" to make this product auto-suggested.
                  </div>
                ) : null;
              }
              return (
                <div data-testid="prod-drawer-match-info" className={`mb-4 ${mi.is_first_match ? "bg-emerald-50 border-emerald-200" : "bg-cyan-50 border-cyan-200"} border rounded-xl p-3 text-xs`}>
                  <div className="font-semibold uppercase tracking-wider text-[10px] mb-1 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    {mi.is_first_match ? "Suggested Product · auto-attached" : "Eligible for auto-match"}
                  </div>
                  <div className="text-slate-700">Matched by: <span className="font-semibold">{mi.labels.join(" · ")}</span></div>
                  {!mi.is_first_match && (
                    <div className="text-[10px] text-slate-500 mt-1 italic">Another active product is picked first for these keywords. Tweak this product's name/category to take priority, or deactivate the competitor.</div>
                  )}
                </div>
              );
            })()}

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-700">Name</Label>
                <Input data-testid="prod-name" value={drawer.name} onChange={(e) => setDrawer({ ...drawer, name: e.target.value })} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Category</Label>
                  <Input value={drawer.category || ""} onChange={(e) => setDrawer({ ...drawer, category: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Default status</Label>
                  <select value={drawer.default_status} onChange={(e) => setDrawer({ ...drawer, default_status: e.target.value })} className="mt-1 w-full h-10 border border-slate-300 rounded-md px-3 text-sm">
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-700">Short description</Label>
                <Textarea data-testid="prod-short-desc" value={drawer.short_description || ""} onChange={(e) => setDrawer({ ...drawer, short_description: e.target.value })} className="mt-1" rows={3} />
                <div className="text-[11px] text-slate-500 mt-1">Shown on estimates under the product thumbnail. Aim for ~140 chars / 2&ndash;3 lines.</div>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold text-slate-700">Long description <span className="text-slate-400 font-normal normal-case">(reserved for future product detail page)</span></Label>
                <Textarea data-testid="prod-long-desc" value={drawer.long_description || ""} onChange={(e) => setDrawer({ ...drawer, long_description: e.target.value })} className="mt-1" rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Detail URL <span className="text-slate-400 font-normal normal-case">(external)</span></Label>
                  <Input data-testid="prod-detail-url" type="url" placeholder="https://pristinewater.com.au/products/protect-uv" value={drawer.detail_url || ""} onChange={(e) => setDrawer({ ...drawer, detail_url: e.target.value })} className="mt-1" />
                  <div className="text-[11px] text-slate-500 mt-1">Adds a &ldquo;View details&rdquo; link on estimates. Leave blank to hide.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Slug <span className="text-slate-400 font-normal normal-case">(reserved)</span></Label>
                  <Input data-testid="prod-slug" placeholder="protect-uv" value={drawer.slug || ""} onChange={(e) => setDrawer({ ...drawer, slug: e.target.value })} className="mt-1" />
                  <div className="text-[11px] text-slate-500 mt-1">Stored for future internal product pages. No link rendered yet.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Unit price (cents)</Label>
                  <Input data-testid="prod-price" type="number" value={drawer.unit_price_cents} onChange={(e) => setDrawer({ ...drawer, unit_price_cents: e.target.value })} className="mt-1" />
                  <div className="text-[11px] text-slate-500 mt-1">= ${((Number(drawer.unit_price_cents) || 0) / 100).toFixed(2)}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-semibold text-slate-700">Default qty</Label>
                  <Input type="number" value={drawer.default_qty} onChange={(e) => setDrawer({ ...drawer, default_qty: e.target.value })} className="mt-1" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!drawer.gst_included} onChange={(e) => setDrawer({ ...drawer, gst_included: e.target.checked })} />
                Price includes GST
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="prod-active-toggle"
                  type="checkbox"
                  checked={!!drawer.active}
                  onChange={(e) => setDrawer({ ...drawer, active: e.target.checked })}
                />
                Active &mdash; available when building proposals
              </label>

              {/* ─────────────────────────────────────────────────────────
                   Feb 2026 — recommendation-key picker.
                   Tag this product with every test-result rule key it
                   satisfies so the proposal engine can auto-attach it
                   deterministically (no keyword-string collisions).
                   Empty selection is valid but flagged in the helper
                   text so admins are nudged to tag their catalog.
              ───────────────────────────────────────────────────────── */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-xs uppercase font-semibold text-slate-700">
                  Matches these test recommendations
                </Label>
                <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
                  When a report fires one of these rules, this product will be auto-attached to the generated proposal.
                  Tag every recommendation this product genuinely addresses — the more tags, the more auto-fills.
                </div>
                {recKeys.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Loading rule keys…</div>
                ) : (
                  <div data-testid="prod-rec-key-picker" className="flex flex-wrap gap-1.5">
                    {recKeys.map((rk) => {
                      const active = (drawer.recommendation_keys || []).includes(rk.key);
                      return (
                        <button
                          key={rk.key}
                          type="button"
                          data-testid={`prod-rec-key-chip-${rk.key}`}
                          onClick={() => {
                            const cur = new Set(drawer.recommendation_keys || []);
                            if (cur.has(rk.key)) cur.delete(rk.key);
                            else cur.add(rk.key);
                            setDrawer({ ...drawer, recommendation_keys: [...cur] });
                          }}
                          title={rk.helper}
                          className={
                            "text-[11px] font-semibold px-2.5 py-1 rounded-full border transition " +
                            (active
                              ? "bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700"
                              : "bg-slate-50 text-slate-700 border-slate-300 hover:border-cyan-500 hover:text-cyan-700")
                          }
                        >
                          {active ? "✓ " : ""}{rk.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {(drawer.recommendation_keys || []).length === 0 && recKeys.length > 0 && (
                  <div className="text-[11px] text-amber-700 mt-2 flex items-start gap-1">
                    <span className="font-semibold">⚠</span>
                    <span>Untagged — this product will only auto-attach via the legacy keyword fallback (fragile). Add at least one tag above for reliable matching.</span>
                  </div>
                )}

                {/* ─────────────────────────────────────────────────────
                     Feb 2026 — inline "Preview matcher" panel.
                     Simulates the exact matcher that
                     `create_proposal_from_test` runs, using this
                     product's currently-selected tags. Lets admins
                     verify coverage + spot competitor products without
                     needing a real test result.
                ───────────────────────────────────────────────────── */}
                {(drawer.recommendation_keys || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] text-slate-500">
                        Simulate the matcher for the tags above — see exactly which products would be attached.
                      </div>
                      <button
                        type="button"
                        data-testid="prod-preview-matcher-btn"
                        onClick={async () => {
                          setPreview({ loading: true, data: null });
                          try {
                            const { data } = await api.post(
                              "/admin/proposals/preview-match",
                              { rule_keys: drawer.recommendation_keys || [] },
                            );
                            setPreview({ loading: false, data });
                          } catch (e) {
                            setPreview({ loading: false, data: null, error: formatApiError(e?.response?.data?.detail) || e?.message });
                          }
                        }}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-600 hover:text-white hover:border-cyan-600 transition inline-flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <Sparkles className="w-3 h-3" /> Preview matcher
                      </button>
                    </div>

                    {preview?.loading && (
                      <div className="mt-2 text-xs text-slate-500 italic">Simulating matcher…</div>
                    )}
                    {preview?.error && (
                      <div className="mt-2 text-xs text-rose-600">{preview.error}</div>
                    )}
                    {preview?.data && (
                      <div data-testid="prod-preview-matcher-result" className="mt-3 space-y-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                          Would attach {preview.data.product_count} product line(s)
                        </div>
                        {(preview.data.matched || []).length > 0 && (
                          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 space-y-1">
                            {(preview.data.matched || []).map((m, i) => {
                              const isThis = m.product_id === drawer.id;
                              return (
                                <div
                                  key={`m-${i}`}
                                  className={
                                    "flex items-center gap-2 text-xs " +
                                    (isThis ? "font-semibold text-emerald-900" : "text-slate-700")
                                  }
                                >
                                  <span className="text-emerald-600">✓</span>
                                  <span className="font-mono text-[10px] uppercase text-emerald-700 bg-white border border-emerald-200 px-1.5 py-0.5 rounded">
                                    {m.key.replace(/_/g, " ")}
                                  </span>
                                  <span className="flex-1 truncate">{m.product_name}</span>
                                  {isThis && (
                                    <span className="text-[9px] uppercase tracking-wider font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">this product</span>
                                  )}
                                  <span className="text-[9px] uppercase tracking-wider text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded">
                                    {m.source === "tag" ? "tag" : "keyword"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(preview.data.skipped || []).length > 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-1">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                              Rules with no product coverage — tag a product for these:
                            </div>
                            {(preview.data.skipped || []).map((s, i) => (
                              <div key={`s-${i}`} className="text-xs text-amber-900 flex items-center gap-2">
                                <span className="text-amber-600">⚠</span>
                                <span className="font-mono text-[10px] uppercase bg-white border border-amber-200 px-1.5 py-0.5 rounded">
                                  {s.key.replace(/_/g, " ")}
                                </span>
                                <span className="flex-1 text-[11px] italic">{s.reason}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Image manager — upload, URL, or remove */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-xs uppercase font-semibold text-slate-700">Thumbnail</Label>
                <div className="mt-2 flex items-start gap-4">
                  {drawer.image_url && !imgBroken ? (
                    <img
                      src={resolveImageUrl(drawer.image_url)}
                      alt=""
                      onError={() => setImgBroken(true)}
                      className="w-24 h-24 object-cover rounded-lg border border-slate-200"
                      data-testid="prod-image-preview"
                    />
                  ) : drawer.image_url && imgBroken ? (
                    <div
                      data-testid="prod-image-broken"
                      className="w-24 h-24 bg-rose-50 rounded-lg border border-rose-200 flex flex-col items-center justify-center text-center text-[10px] text-rose-700 px-2"
                      title={`Failed to load: ${drawer.image_url}`}
                    >
                      <ImageIcon className="w-5 h-5 text-rose-400 mb-1" />
                      Image failed<br />to load
                    </div>
                  ) : (
                    <div
                      data-testid="prod-image-placeholder"
                      className="w-24 h-24 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center"
                    >
                      <ImageIcon className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 space-y-2.5">
                    <p className="text-xs text-slate-500">
                      {drawer.id
                        ? "PNG / JPEG / WebP up to 5 MB. Replaces the current image."
                        : "Save the product first, then add an image."}
                    </p>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid="prod-upload"
                        type="button"
                        variant="outline"
                        disabled={!drawer.id}
                        onClick={() => fileRef.current?.click()}
                        className="rounded-full h-8 text-xs"
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload image
                      </Button>
                      {drawer.image_url && (
                        <Button
                          data-testid="prod-image-remove"
                          type="button"
                          variant="outline"
                          onClick={removeImage}
                          className="rounded-full h-8 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove image
                        </Button>
                      )}
                    </div>
                    <div className="flex items-stretch gap-2 pt-1">
                      <Input
                        data-testid="prod-image-url-input"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        placeholder="https://cdn.example.com/product.jpg"
                        disabled={!drawer.id}
                        className="flex-1 text-xs h-8 rounded-lg"
                      />
                      <Button
                        data-testid="prod-image-use-url"
                        type="button"
                        disabled={!drawer.id || !imageUrlInput.trim()}
                        onClick={() => applyImageUrl(imageUrlInput)}
                        className="rounded-full h-8 text-xs bg-cyan-600 hover:bg-cyan-500 text-white"
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1.5" /> Use URL
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-relaxed">
                      Accepts http(s) URLs ending in .png / .jpg / .jpeg / .webp, or one of our own
                      <code className="bg-slate-100 px-1 rounded mx-0.5">/api/uploads/products/</code>
                      paths.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDrawer(null)} disabled={saving} className="rounded-full">Cancel</Button>
              <Button data-testid="prod-save" onClick={save} disabled={saving || !drawer.name} className="rounded-full font-semibold">
                <Save className="w-4 h-4 mr-1.5" /> {drawer.id ? "Save changes" : "Create product"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
